import React, { useState } from 'react'
import {
  CalendarDays,
  CheckCircle2,
  FileText,
  Image as ImageIcon,
  Info,
  MessageSquareText,
  Minus,
  Plus,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  Users,
} from 'lucide-react'
import { FaInstagram, FaTiktok } from 'react-icons/fa6'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  DELIVERABLES,
  INCLUDED_LICENSE_DAYS,
  LICENSE_TERMS,
  licenseSavings,
  formatRange,
  type DeliverableId,
  type LicenseTerm,
  type RateCard,
} from '@adthlete/common/utils/dealPricing'
import { bookingTotal, midpoint, type Booking } from './booking'

const money = (n: number) => `$${n.toLocaleString('en-US')}`

const DELIVERABLE_ICON: Record<DeliverableId, React.ReactNode> = {
  ig_reel: <FaInstagram className="h-5 w-5" />,
  tiktok_video: <FaTiktok className="h-5 w-5" />,
  ig_story: <FaInstagram className="h-5 w-5" />,
  static_post: <ImageIcon className="h-5 w-5" />,
  appearance: <Users className="h-5 w-5" />,
}

const inputCls =
  'h-12 w-full rounded-xl border border-border bg-card px-4 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40 disabled:bg-muted disabled:text-muted-foreground'

/** The two ways to book, as big tabs: from their rate card, or a custom request */
const KindTabs: React.FC<{
  value: Booking['kind']
  onChange: (k: Booking['kind']) => void
  hasCard: boolean
  first: string
}> = ({ value, onChange, hasCard, first }) => (
  <div role="tablist" aria-label="What are you booking?" className="grid gap-3 sm:grid-cols-2">
    {(
      [
        {
          k: 'rate_card' as const,
          icon: FileText,
          title: 'From their shop',
          sub: hasCard ? `Choose from ${first}'s prices` : `${first} hasn't set prices yet`,
        },
        {
          k: 'custom' as const,
          icon: Sparkles,
          title: 'Custom request',
          sub: 'Name your price or ask for a quote',
        },
      ] as const
    ).map((t) => (
      <button
        key={t.k}
        type="button"
        role="tab"
        aria-selected={value === t.k}
        disabled={t.k === 'rate_card' && !hasCard}
        onClick={() => onChange(t.k)}
        className={cn(
          'flex items-center gap-4 rounded-xl border px-4 py-3.5 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
          value === t.k
            ? 'border-[#9BD85A] bg-[#D9FBA9]'
            : 'border-border bg-card hover:border-[#C9E9A6]',
        )}
      >
        <t.icon className="h-7 w-7 shrink-0 text-foreground" />
        <span className="min-w-0">
          <span className="block font-semibold text-foreground">{t.title}</span>
          <span className="block text-xs text-muted-foreground">{t.sub}</span>
        </span>
      </button>
    ))}
  </div>
)

/** "Includes 30 days paid usage", with the option to extend at the athlete's price */
const UsageCard: React.FC<{
  value: Booking
  set: (p: Partial<Booking>) => void
  card: RateCard | null | undefined
  first: string
  custom?: boolean
}> = ({ value: b, set, card, first, custom }) => {
  const [open, setOpen] = useState(!!b.licenseDays)
  const prices = Object.fromEntries(
    LICENSE_TERMS.map((t) => [t.days, card?.licensing[t.days]?.amount ?? 0]),
  ) as Record<LicenseTerm, number>
  const canExtend = !custom && !!card && b.deliverable !== 'appearance'
  return (
    <div className="rounded-xl border border-[#DCEBC8] bg-[#F4FBEA] px-4 py-3">
      <div className="flex items-center gap-4">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#E3F5CC]">
          <ShieldCheck className="h-5 w-5 text-foreground" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-foreground">
            Includes {INCLUDED_LICENSE_DAYS} days paid usage
            {b.licenseDays ? ` + ${b.licenseDays} more` : ''}
          </p>
          <p className="text-sm text-muted-foreground">
            Every {custom ? 'custom ' : ''}deliverable on Athios includes {INCLUDED_LICENSE_DAYS}{' '}
            days of paid usage licensing.
          </p>
        </div>
        {canExtend ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="shrink-0 text-sm font-semibold text-[#3F7A0A] hover:underline"
          >
            {open ? 'Hide' : 'Extend'}
          </button>
        ) : null}
      </div>
      {canExtend && open ? (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
          {[null, ...LICENSE_TERMS.map((t) => t.days)].map((days) => {
            const t = LICENSE_TERMS.find((x) => x.days === days)
            const on = (b.licenseDays ?? null) === days
            const save = days ? licenseSavings(prices, days) : 0
            return (
              <button
                key={days ?? 'none'}
                type="button"
                aria-pressed={on}
                onClick={() => set({ licenseDays: days })}
                className={cn(
                  'rounded-lg border px-2 py-2 text-center text-xs',
                  on ? 'border-[#9BD85A] bg-card' : 'border-transparent bg-card/60 hover:bg-card',
                )}
              >
                <span className="block text-muted-foreground">{t ? t.label : 'No extension'}</span>
                <span className="block font-semibold tabular-nums text-foreground">
                  {days ? `+${money(prices[days])}` : 'Included'}
                </span>
                {save >= 0.05 ? (
                  <span className="block text-xs font-semibold text-[#3F7A0A]">
                    Save {Math.round(save * 100)}%
                  </span>
                ) : null}
              </button>
            )
          })}
          <p className="col-span-full text-xs text-muted-foreground">
            Extensions are per {b.deliverable === 'ig_story' ? 'set' : 'post'}, at {first}'s rate
            card price. You can also extend later.
          </p>
        </div>
      ) : null}
    </div>
  )
}

const DateField: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => (
  <div>
    <label htmlFor="offer-due" className="mb-2 block font-semibold text-foreground">
      Content due date <span className="font-normal text-muted-foreground">(optional)</span>
    </label>
    <div className="relative">
      <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
      <input
        id="offer-due"
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(inputCls, 'pl-12')}
      />
    </div>
  </div>
)

/**
 * The offer itself: book a deliverable in one of the athlete's themes at their rate-card price,
 * or make a custom request (off the rate card) with a price or a request for theirs. Every
 * offer includes 30 days of paid usage.
 */
export const BookingPicker: React.FC<{
  value: Booking
  onChange: (b: Booking) => void
  rateCard: RateCard | null | undefined
  athleteFirstName: string
  dueDate: string
  onDueDate: (v: string) => void
}> = ({ value: b, onChange, rateCard: card, athleteFirstName: first, dueDate, onDueDate }) => {
  const set = (patch: Partial<Booking>) => onChange({ ...b, ...patch })
  const range = card?.deliverables[b.deliverable]
  const unit = Number(b.unitPrice) || 0
  const onCard = range ? unit >= range.low && unit <= Math.max(range.high, range.low) : false
  const total = bookingTotal(b, card)

  return (
    <div className="space-y-5">
      <KindTabs value={b.kind} onChange={(kind) => set({ kind })} hasCard={!!card} first={first} />

      {b.kind === 'rate_card' && card ? (
        <>
          {card.themes?.length ? (
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              <span className="font-semibold text-foreground">Theme</span>
              <div className="flex flex-wrap gap-2">
                {card.themes.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={b.themeId === t.id}
                    onClick={() => set({ themeId: t.id })}
                    className={cn(
                      'rounded-full border px-4 py-2 text-base font-medium transition-colors',
                      b.themeId === t.id
                        ? 'border-[#C0FD72] bg-[#C0FD72] text-[#0F172A]'
                        : 'border-border bg-card text-foreground hover:border-[#9BD85A]',
                    )}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <fieldset>
            <legend className="mb-2 font-semibold text-foreground">Deliverable</legend>
            <div className="space-y-2" role="radiogroup">
              {DELIVERABLES.map((d) => {
                const e = card.deliverables[d.id]
                // The slots stay in the same order on every card; one they don't offer is greyed
                const offered = !!e && e.high > 0
                const on = b.deliverable === d.id
                return (
                  <button
                    key={d.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={!offered}
                    onClick={() => set({ deliverable: d.id, unitPrice: String(midpoint(e)) })}
                    className={cn(
                      'flex w-full items-center gap-4 rounded-xl border px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                      on
                        ? 'border-[#9BD85A] bg-[#F4FBEA]'
                        : 'border-border bg-card hover:bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2',
                        on ? 'border-[#0F172A]' : 'border-[#CBD5E1]',
                      )}
                    >
                      {on ? <span className="h-2.5 w-2.5 rounded-full bg-[#0F172A]" /> : null}
                    </span>
                    <span className="shrink-0 text-foreground">{DELIVERABLE_ICON[d.id]}</span>
                    <span className="min-w-0 flex-1 text-base text-foreground">
                      {d.label} <span className="text-sm text-muted-foreground">{d.unit}</span>
                    </span>
                    <span className="shrink-0 font-semibold tabular-nums text-foreground">
                      {offered ? formatRange(e) : 'Not offered'}
                    </span>
                  </button>
                )
              })}
            </div>
          </fieldset>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <p className="mb-2 font-semibold text-foreground">
                {b.deliverable === 'appearance' ? 'Hours' : 'Quantity'}
              </p>
              <div className="inline-flex h-12 items-stretch overflow-hidden rounded-xl border border-border bg-card">
                <button
                  type="button"
                  aria-label="Fewer"
                  onClick={() => set({ quantity: Math.max(1, b.quantity - 1) })}
                  className="px-4 text-foreground hover:bg-muted"
                >
                  <Minus className="h-5 w-5" />
                </button>
                <span className="flex w-16 items-center justify-center border-x border-border text-lg font-semibold tabular-nums text-foreground">
                  {b.quantity}
                </span>
                <button
                  type="button"
                  aria-label="More"
                  onClick={() => set({ quantity: Math.min(20, b.quantity + 1) })}
                  className="px-4 text-foreground hover:bg-muted"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="price-each" className="mb-2 block font-semibold text-foreground">
                Price each
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <input
                  id="price-each"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  value={b.unitPrice}
                  onChange={(e) => set({ unitPrice: e.target.value })}
                  className={cn(inputCls, 'pl-9 tabular-nums')}
                />
              </div>
            </div>
          </div>

          {range ? (
            <p
              className={cn(
                'flex items-center gap-3 rounded-xl px-4 py-3 text-sm',
                onCard ? 'bg-[#F4FBEA] text-foreground' : 'bg-[#FFF7E6] text-[#92400E]',
              )}
            >
              {onCard ? (
                <CheckCircle2 className="h-5 w-5 shrink-0 fill-[#22C55E] text-[#FFFFFF]" />
              ) : (
                <TriangleAlert className="h-5 w-5 shrink-0 text-[#D97706]" />
              )}
              {onCard
                ? `${money(unit)} is within ${first}'s price range (${formatRange(range)}), so it's quick to accept.`
                : unit < range.low
                  ? `${money(unit)} is below ${first}'s price range (${formatRange(range)}). Expect a counter.`
                  : `${money(unit)} is above ${first}'s price range (${formatRange(range)}).`}
            </p>
          ) : null}

          <UsageCard value={b} set={set} card={card} first={first} />
          <DateField value={dueDate} onChange={onDueDate} />

          <div className="flex items-center justify-between border-t border-border pt-4">
            <span className="text-lg font-bold text-foreground">Total</span>
            <span className="text-2xl font-bold tabular-nums text-foreground">
              {money(total ?? 0)}
            </span>
          </div>
        </>
      ) : (
        <>
          <div>
            <label htmlFor="custom-what" className="mb-2 block font-semibold text-foreground">
              What do you want made?
            </label>
            <div className="relative">
              <textarea
                id="custom-what"
                rows={4}
                maxLength={500}
                value={b.description}
                onChange={(e) => set({ description: e.target.value })}
                placeholder="An unboxing Reel of our new flavours, or a TikTok reviewing our product."
                className="w-full resize-none rounded-xl border border-border bg-card px-4 py-3 pb-7 text-base text-foreground outline-none placeholder:text-muted-foreground focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40"
              />
              <span className="pointer-events-none absolute bottom-3 right-4 text-xs tabular-nums text-muted-foreground">
                {b.description.length}/500
              </span>
            </div>
            <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4 shrink-0" />
              This is a custom request, outside {first}'s standard prices.
            </p>
          </div>

          <div className="flex items-center gap-4 rounded-xl border border-[#DCEBC8] bg-[#F4FBEA] px-4 py-3">
            <MessageSquareText className="h-7 w-7 shrink-0 text-foreground" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">Ask for {first}'s rate</p>
              <p className="text-sm text-muted-foreground">
                Send your request and let {first} provide a quote for this content.
              </p>
            </div>
            <Switch
              checked={b.askForRate}
              onCheckedChange={(on) => set({ askForRate: on })}
              aria-label={`Ask for ${first}'s rate`}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="custom-offer" className="mb-2 block font-semibold text-foreground">
                Your offer{' '}
                {b.askForRate ? (
                  <span className="font-normal text-muted-foreground">(not needed)</span>
                ) : null}
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
                  $
                </span>
                <input
                  id="custom-offer"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  disabled={b.askForRate}
                  value={b.askForRate ? '' : b.customAmount}
                  onChange={(e) => set({ customAmount: e.target.value })}
                  placeholder={b.askForRate ? `${first} will quote` : 'e.g. 350'}
                  className={cn(inputCls, 'pl-9 tabular-nums')}
                />
              </div>
              <p className="mt-2 text-sm text-muted-foreground">
                {b.askForRate
                  ? `Turn off "Ask for ${first}'s rate" to name your price.`
                  : `Or ask for ${first}'s rate instead of naming a price.`}
              </p>
            </div>
            <DateField value={dueDate} onChange={onDueDate} />
          </div>

          <UsageCard value={b} set={set} card={card} first={first} custom />

          <div className="border-t border-border pt-4">
            <p className="flex items-center gap-1.5 font-semibold text-foreground">
              Total <Info className="h-4 w-4 text-muted-foreground" />
            </p>
            {total === null ? (
              <>
                <p className="text-2xl font-bold text-foreground">Awaiting athlete quote</p>
                <p className="text-sm text-muted-foreground">
                  You'll get a quote from {first} after sending your request.
                </p>
              </>
            ) : (
              <p className="text-2xl font-bold tabular-nums text-foreground">{money(total)}</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default BookingPicker
